import PortalIdParser from 'PortalIdParser';
import { FileTypes, ThumbnailSize, ThumbnailSizes } from '../constants';
import { ALLOWED_THUMBNAILS_EXTENSIONS } from '../constants/fileTypes';
import {
  appendStaticAppInfo,
  getApiHostForEnvironment,
} from '../internal/utils';
import FMFile from '../types/FMFile';
import {
  getFileType,
  getIsFileExternal,
  getIsFilePrivate,
  getIsFileSensitive,
  getIsSvg,
  getIsSvgTooLarge,
  getIsTemporaryUploadingFile,
  splitNameAndExtension,
} from './file';
import { FileMeta } from '../../__generated__/chirp/com/hubspot/filemanager/rpc/FileMetaFetchRpc';
import { isChirpFileMeta } from '../types/internal';

export function isImage(file: File) {
  return file.type && file.type.startsWith('image/');
}

const NO_LOCAL_PREVIEW_IMAGE_EXTENSIONS = ['svg', 'heic', 'tiff'];

export function canPreviewLocalFile(localFile: File) {
  const { extension } = splitNameAndExtension(localFile.name);
  return (
    isImage(localFile) &&
    !NO_LOCAL_PREVIEW_IMAGE_EXTENSIONS.includes(extension.toLowerCase())
  );
}

const getHasThumbnailForFMFile = (file: FMFile): boolean => {
  if (
    getIsSvg(file.type, file.extension, file.encoding) &&
    (getIsSvgTooLarge(file.size) || getIsTemporaryUploadingFile(file.id))
  ) {
    return false;
  }
  if (getIsFileExternal(file.meta, file.url)) {
    return false;
  }
  if (getIsFileSensitive(file.meta)) {
    return false;
  }
  const fileType = getFileType(file.type, file.extension);
  return (
    fileType === FileTypes.IMG ||
    fileType === FileTypes.MOVIE ||
    ALLOWED_THUMBNAILS_EXTENSIONS.includes(file.extension)
  );
};

const getHasThumbnailForFileMeta = (file: FileMeta): boolean => {
  if (
    getIsSvg(file.fileType, file.extension, file.encoding) &&
    (getIsSvgTooLarge(file.size) || getIsTemporaryUploadingFile(file.id))
  ) {
    return false;
  }

  if (getIsFileExternal(file.access, file.accessUrl)) {
    return false;
  }
  if (getIsFileSensitive(file.access)) {
    return false;
  }
  const fileType = getFileType(file.fileType, file.extension);
  return (
    fileType === FileTypes.IMG ||
    fileType === FileTypes.MOVIE ||
    ALLOWED_THUMBNAILS_EXTENSIONS.includes(file.extension)
  );
};

export const getHasThumbnail = (file: FMFile | FileMeta): boolean => {
  return isChirpFileMeta(file)
    ? getHasThumbnailForFileMeta(file)
    : getHasThumbnailForFMFile(file);
};

export function getThumbnailUrl(
  fileId: number,
  thumbSize: ThumbnailSize = ThumbnailSizes.THUMB,
  isFilePrivate = false,
  options: { isFallbackImageDisabled?: boolean; timestamp?: number } = {}
) {
  const { isFallbackImageDisabled, timestamp } = options;

  let path = `//${getApiHostForEnvironment()}/filemanager/api/v3/files/thumbnail-redirect/${fileId}`;
  if (isFilePrivate) {
    path = `//${getApiHostForEnvironment()}/filemanager/api/v2/files/${fileId}/signed-url-redirect`;
  }

  let url = `${path}?size=${thumbSize}&portalId=${PortalIdParser.get()}`;

  if (isFallbackImageDisabled) {
    url = `${url}&errorOnPlaceholder=true`;
  }

  if (timestamp !== undefined) {
    url = `${url}&t=${timestamp}`;
  }

  return appendStaticAppInfo(url);
}

export function getFileThumbnailUrl(
  file: FMFile | FileMeta,
  options: {
    isFallbackImageDisabled?: boolean;
    thumbnailSize?: ThumbnailSize;
    timestamp?: number;
  } = {}
): string | undefined {
  const { isFallbackImageDisabled, thumbnailSize, timestamp } = options;
  const isFileMeta = isChirpFileMeta(file);
  const isPrivate = isFileMeta
    ? getIsFilePrivate(file.access, file.accessUrl)
    : getIsFilePrivate(file.meta, file.url);
  const hasThumbnail = getHasThumbnail(file);

  if (!hasThumbnail) {
    return undefined;
  }

  return getThumbnailUrl(file.id, thumbnailSize, isPrivate, {
    isFallbackImageDisabled,
    timestamp,
  });
}
